import React, { createContext, useMemo, useContext, useCallback, useState } from 'react';
import PropTypes from 'prop-types';

import { useAuth } from '../../authentication';
import { useGetAllOrganizationQuestions } from '../hooks';
import { useOrganizations } from './OrganizationsProvider';

const QuestionReviewContext = createContext();

const QuestionReviewProvider = ({ children = null }) => {
  const { accessToken } = useAuth();
  const { currentOrganization } = useOrganizations();
  const [currentPage, setCurrentPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState('');

  // Use the existing hook for the current page and search query
  const questionsQuery = useGetAllOrganizationQuestions(
    accessToken,
    currentOrganization?.id,
    currentPage,
    searchQuery
  );

  const fetchPage = useCallback(
    (page) => {
      if (page !== currentPage) {
        setCurrentPage(page);
      }
    },
    [currentPage]
  );

  const handleSearch = useCallback((query) => {
    setSearchQuery(query);
    // Reset to page 1 when search changes
    setCurrentPage(1);
  }, []);

  const value = useMemo(() => {
    const questions = questionsQuery.data?.data || [];

    // FIXED: Parse the server response correctly
    // Server returns: {data: Array, nextPage: number|null, prevPage: number|null}
    const hasNextPage =
      questionsQuery.data?.nextPage !== null && questionsQuery.data?.nextPage !== undefined;

    return {
      questions,
      hasNextPage,
      isLoading: questionsQuery.isLoading,
      handlePaginationChanged: fetchPage,
      handleSearch,
      searchQuery,
      currentPage
    };
  }, [
    questionsQuery.data,
    questionsQuery.isLoading,
    fetchPage,
    handleSearch,
    searchQuery,
    currentPage
  ]);

  return <QuestionReviewContext.Provider value={value}>{children}</QuestionReviewContext.Provider>;
};

QuestionReviewProvider.propTypes = {
  children: PropTypes.node
};

const useQuestionReview = () => useContext(QuestionReviewContext);

export { QuestionReviewContext, QuestionReviewProvider, useQuestionReview };
